import React, { useRef } from "react";
import type { FieldValues } from "react-hook-form";
import i18n from "i18next";
import type { UseAavoFormReturn } from "src/components/common/forms/useAavoForm/types.ts";
import type {
	AgentFormContext,
	AgentFormInputErrorSnapshot,
} from "src/components/views/ai/assistant/uiContext/agentUiNodeTypes.ts";
import { AgentUiScopeNode } from "src/components/views/ai/assistant/uiContext/AgentUiNode.tsx";
import { truncateAgentFormInputValues } from "src/components/views/ai/assistant/uiContext/agentFormInputTruncation.ts";
import { redactAgentFormInputValues } from "src/components/views/ai/assistant/uiContext/agentFormInputRedaction.ts";
import { AgentFormSecretInputsContext } from "src/components/views/ai/assistant/uiContext/agentFormSecretInputs.ts";
import { toJsonObject } from "src/components/views/ai/assistant/uiContext/jsonValueUtils.ts";

export interface AgentFormUiContextProps<TFieldValues extends FieldValues, TResult> extends React.PropsWithChildren {
	agentContext?: AgentFormContext<TFieldValues>;
	form: UseAavoFormReturn<TFieldValues, TResult>;
}

export function AgentFormUiContext<TFieldValues extends FieldValues, TResult>({
	agentContext,
	children,
	form,
}: AgentFormUiContextProps<TFieldValues, TResult>) {
	const secretInputsRef = useRef(new Set<string>());
	return (
		<AgentUiScopeNode
			node={() => {
				const values = form.getValues();
				const secretPaths = [...secretInputsRef.current, ...(agentContext?.secretInputPaths?.(values) ?? [])];
				return {
					kind: "form",
					key: agentContext?.key,
					title: agentContext?.title,
					pathSegments: agentContext?.pathSegments ?? [i18n.t("form")],
					data: agentContext?.data,
					formState: {
						inputs: truncateAgentFormInputValues(
							redactAgentFormInputValues(toJsonObject(values), secretPaths),
						),
						inputErrors: flattenFormErrors(form.formState.errors),
						isDirty: form.formState.isDirty,
						isValid: form.formState.isValid,
						disabled: form.formState.disabled,
					},
				};
			}}
		>
			<AgentFormSecretInputsContext.Provider value={secretInputsRef.current}>
				{children}
			</AgentFormSecretInputsContext.Provider>
		</AgentUiScopeNode>
	);
}

function flattenFormErrors(errors: unknown): Record<string, AgentFormInputErrorSnapshot> {
	const result: Record<string, AgentFormInputErrorSnapshot> = {};
	collectFormErrors(errors, "", result);
	return result;
}

function collectFormErrors(value: unknown, path: string, result: Record<string, AgentFormInputErrorSnapshot>) {
	if (value == null || typeof value !== "object") return;
	const record = value as Record<string, unknown>;
	const message = record.message;
	const type = record.type;
	const types = record.types;
	if ((typeof message === "string" || typeof type === "string") && path !== "") {
		result[path] = {
			message: typeof message === "string" ? message : undefined,
			type: typeof type === "string" ? type : undefined,
			types: stringifyErrorTypes(types),
		};
	}
	for (const [key, childValue] of Object.entries(record)) {
		if (key === "message" || key === "type" || key === "types" || key === "ref") continue;
		collectFormErrors(childValue, path === "" ? key : `${path}.${key}`, result);
	}
}

function stringifyErrorTypes(value: unknown): Record<string, string> | undefined {
	if (value == null || typeof value !== "object") return undefined;
	const result: Record<string, string> = {};
	for (const [key, entryValue] of Object.entries(value)) {
		result[key] = String(entryValue);
	}
	return result;
}
